import { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authApi } from '@/api/endpoints/auth';
import type { LoginRequest, Manager } from '@/api/endpoints/auth';
import { connectSocket, disconnectSocket } from '@/api/socket';

interface AuthContextType {
  manager: Manager | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  manager: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [manager, setManager] = useState<Manager | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('manager_token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const { manager: managerData } = await authApi.getMe();
      setManager(managerData);
      connectSocket(token);
    } catch (error) {
      localStorage.removeItem('manager_token');
      localStorage.removeItem('manager_info');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginRequest) => {
    const response = await authApi.login(credentials);
    localStorage.setItem('manager_token', response.token);
    localStorage.setItem('manager_info', JSON.stringify(response.manager));
    setManager({
      ...response.manager,
      created_at: new Date().toISOString(),
      last_login: new Date().toISOString(),
    });
    connectSocket(response.token);
    window.location.href = '/';
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      disconnectSocket();
      localStorage.removeItem('manager_token');
      localStorage.removeItem('manager_info');
      setManager(null);
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider
      value={{
        manager,
        isAuthenticated: !!manager,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
